import "@uploadcare/react-uploader/core.css";
import Head from 'next/head';
import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import type { AppProps /*, AppContext */ } from 'next/app';
import { Inter } from 'next/font/google';
import { ThemeProvider } from 'next-themes';
import { Toaster } from 'react-hot-toast';

import '../styles/globals.css';
import { init } from '../lib/sentry';
import { AuthProvider, PageAuth, initializeAmplify } from '../lib/auth';
import { ApolloProvider } from '@apollo/client';
import { useApollo } from '../lib/graphql/apollo';
import { Meta } from '../lib/ui';
import * as gtag from '../lib/gtag';
import { initPosthog } from '../lib/posthog';
import { initMixpanel } from '../lib/mixpanel';
import { sanitizePathForAnalytics } from '../lib/sanitize-analytics-path';
import { FeatureAnnouncements } from '../lib/announcements';

init();
initializeAmplify();

const inter = Inter({
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-inter',
});

const DefaultLayout = ({ children }) => children;

function GlobalApp({ Component, pageProps }: AppProps) {
  const apolloClient = useApollo(pageProps);
  const router = useRouter();
  useEffect(() => {
    initPosthog();
    initMixpanel();
    const safePath = sanitizePathForAnalytics(router.asPath);
    gtag.pageview(safePath);

    const handleRouteChange = (url: string) => {
      const next = sanitizePathForAnalytics(url);
      gtag.pageview(next);
    };

    const handleRouteError = () => {
      return;
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    router.events.on("routeChangeError", handleRouteError);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      router.events.off("routeChangeError", handleRouteError);
    };
  }, [router.events]);
  const {
    Layout = DefaultLayout,
    requireLogin,
    layoutOptions,
  } = Component as any;
  return (
    <ThemeProvider attribute="class" defaultTheme="system" disableTransitionOnChange>
    <AuthProvider>
      {/* Set Inter on <html> so portalled UI (modals, dropdowns) inherits it too */}
      <style jsx global>{`
        html {
          --font-inter: ${inter.style.fontFamily};
          font-family: ${inter.style.fontFamily}, -apple-system, BlinkMacSystemFont,
            'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }
      `}</style>
      <ApolloProvider client={apolloClient}>
        <PageAuth enabled={requireLogin}>
          <Layout options={layoutOptions}>
            <Head>
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1, shrink-to-fit=no"
              />
              <link rel="icon" href="/favicon.ico" />
            </Head>
            <Meta />
            <Component {...pageProps} />
            <FeatureAnnouncements />
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: 'rgb(var(--surface))',
                  color: 'rgb(var(--neutral-900))',
                  border: '1px solid var(--border-standard)',
                  boxShadow:
                    '0 4px 12px rgba(var(--shadow-rgb), 0.08), 0 1px 3px rgba(var(--shadow-rgb), 0.04)',
                  fontSize: '14px',
                  lineHeight: '20px',
                },
                success: {
                  duration: 2500,
                },
                error: {
                  duration: 5000,
                },
                blank: {
                  duration: 5000,
                }
              }}
            />
          </Layout>
        </PageAuth>
      </ApolloProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default GlobalApp;
